import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import ottoRocket from "../../assets/imgs/onboarding/otto-in-rocket.png";
import moon from "../../assets/imgs/onboarding/scribbles-on-the-moon.png";
import starrySky from "../../assets/imgs/onboarding/starry-sky.svg";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const TIME_STATS = [
  { value: "1-2", label: "hours saved", sublabel: "Your first day" },
  { value: "10+", label: "hours saved", sublabel: "Your first week" },
  {
    value: "50+",
    label: "hours saved",
    sublabel: "Your first month",
  },
];

// ---------------------------------------------------------------------------
// Animated number counter
// ---------------------------------------------------------------------------
function AnimatedNumber({ value, startDelay = 0 }) {
  const numericPart = parseInt(value, 10);
  const suffix = value.replace(String(numericPart), "");
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(timer);
  }, [startDelay]);

  useEffect(() => {
    if (!started) return;
    const duration = 1400;
    const steps = 40;
    const increment = numericPart / steps;
    const interval = duration / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      if (step >= steps) {
        setCurrent(numericPart);
        clearInterval(timer);
      } else {
        setCurrent(Math.round(increment * step));
      }
    }, interval);

    return () => clearInterval(timer);
  }, [started, numericPart]);

  return (
    <span>
      {started ? current : 0}
      {suffix}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Reveal wrapper
// ---------------------------------------------------------------------------
function Reveal({ delay = 0, children, className = "" }) {
  return (
    <div
      className={`opacity-0 ${className}`}
      style={{
        animation: `welcome-reveal 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms forwards`,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function NewOnboardingLanding() {
  const navigate = useNavigate();
  const [isLaunching, setIsLaunching] = useState(false);

  const [stars] = useState(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 70}%`,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 4,
      duration: Math.random() * 3 + 2,
    })),
  );

  const handleLaunch = () => {
    if (isLaunching) return;
    setIsLaunching(true);
    mixpanel.track("Onboarding: Welcome CTA Clicked");
    setTimeout(
      () =>
        navigate("/dashboard", { state: { fromOnboarding: true } }),
      1800,
    );
  };

  return (
    <div
      className="fixed inset-0 w-screen h-screen z-modal flex flex-col overflow-hidden"
      style={{
        background:
          "linear-gradient(to bottom, #0f0f23 0%, #1a1a3e 40%, #0f0f23 100%)",
      }}
    >
      {/* Starry sky */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${starrySky})`,
          backgroundRepeat: "repeat-x",
          backgroundPosition: "top",
          backgroundSize: "auto",
          opacity: 0.8,
        }}
      />

      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-white pointer-events-none"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
          }}
        />
      ))}

      {/* Radial glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: "900px",
          height: "900px",
          left: "50%",
          top: "38%",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 60%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 md:px-12">
        {/* Hero */}
        <Reveal delay={200} className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight font-title">
            Less paperwork starts{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #818cf8, #a78bfa, #c4b5fd)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              now.
            </span>
          </h1>
        </Reveal>

        <Reveal delay={400} className="text-center mt-3 md:mt-4">
          <p className="text-base md:text-lg text-white/50">
            Scribenote takes care of your note-taking, and much more.
          </p>
        </Reveal>

        {/* Time stats */}
        <Reveal delay={600} className="mt-12 md:mt-16">
          <div className="flex justify-center items-end gap-8 md:gap-14">
            {TIME_STATS.map((stat, i) => (
              <div
                key={stat.sublabel}
                className="relative text-center"
              >
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(129,140,248,0.12) 0%, transparent 70%)",
                    transform: "scale(2.5) translateY(-10%)",
                  }}
                />
                <div
                  className="relative text-5xl md:text-7xl font-extrabold font-title"
                  style={{
                    background:
                      "linear-gradient(180deg, #ffffff 0%, var(--color-indigo-400) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  <AnimatedNumber
                    value={stat.value}
                    startDelay={800 + i * 300}
                  />
                </div>
                <div className="text-xs md:text-sm text-white/50 mt-1">
                  {stat.label}
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  {stat.sublabel}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* CTA */}
        <Reveal
          delay={1200}
          className="mt-14 md:mt-16 flex flex-col items-center"
        >
          <button
            type="button"
            onClick={handleLaunch}
            disabled={isLaunching}
            className={`
              cta-button group whitespace-nowrap
              flex flex-col items-center
              px-10 md:px-12 py-4 md:py-5 mb-md rounded-full
              font-bold
              transition-all duration-300 outline-none
              focus-visible:ring-4 focus-visible:ring-white/20
              ${
                isLaunching
                  ? "bg-success scale-105"
                  : "bg-brand hover:bg-brand-hover hover:scale-[1.03]"
              }
            `}
            data-testid="welcome-cta"
          >
            <span className="text-white text-lg">Let&apos;s Go</span>
          </button>
        </Reveal>
      </div>

      {/* Moon */}
      <img
        src={moon}
        alt=""
        className="absolute right-0 bottom-0 pointer-events-none select-none z-decorator"
        style={{ width: "clamp(180px, 30vw, 700px)", height: "auto" }}
        draggable={false}
      />

      {/* Otto */}
      <img
        src={ottoRocket}
        alt=""
        className={`
          absolute pointer-events-none select-none z-control hidden md:block
          ${isLaunching ? "rocket-launching" : "rocket-entering"}
        `}
        style={{ width: "clamp(100px, 12vw, 280px)", height: "auto" }}
        draggable={false}
      />

      {/* Animations */}
      <style>{`
        @keyframes welcome-reveal {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.2; }
          50%      { opacity: 0.9; }
        }

        @keyframes cta-breathe {
          0%, 100% { box-shadow: 0 4px 30px color-mix(in srgb, var(--color-indigo-500) 30%, transparent), 0 0 60px color-mix(in srgb, var(--color-indigo-500) 8%, transparent); }
          50%      { box-shadow: 0 4px 50px color-mix(in srgb, var(--color-indigo-500) 50%, transparent), 0 0 100px color-mix(in srgb, var(--color-indigo-500) 15%, transparent); }
        }

        .cta-button:not(:disabled) {
          animation: cta-breathe 3s ease-in-out infinite;
        }

        .cta-button:hover {
          animation: none;
          box-shadow: 0 4px 50px color-mix(in srgb, var(--color-indigo-500) 50%, transparent), 0 0 100px color-mix(in srgb, var(--color-indigo-500) 20%, transparent) !important;
        }

        .rocket-entering {
          left: 3vw;
          bottom: -20vh;
          animation: rocket-drift-in 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.6s forwards,
                     rocket-idle 6s ease-in-out 1.8s infinite;
        }

        .rocket-launching {
          left: 3vw;
          bottom: 12vh;
          animation: rocket-blast 1.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards !important;
        }

        @keyframes rocket-drift-in {
          from { bottom: -20vh; opacity: 0; }
          to   { bottom: 12vh; opacity: 1; }
        }

        @keyframes rocket-idle {
          0%   { transform: translateY(0) rotate(0deg); }
          25%  { transform: translateY(-8px) rotate(3deg); }
          50%  { transform: translateY(0) rotate(0deg); }
          75%  { transform: translateY(-5px) rotate(-2deg); }
          100% { transform: translateY(0) rotate(0deg); }
        }

        @keyframes rocket-blast {
          0% {
            bottom: 12vh;
            left: 3vw;
            transform: rotate(0deg);
            opacity: 1;
          }
          40% {
            bottom: 45vh;
            left: 25vw;
            transform: rotate(30deg);
            opacity: 1;
          }
          100% {
            bottom: 110vh;
            left: 80vw;
            transform: rotate(55deg);
            opacity: 0;
          }
        }

        /* Respect users who prefer reduced motion */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          .rocket-entering {
            left: 3vw;
            bottom: 12vh;
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
